<template lang="pug">
.stickers-block(v-if="stickersBlock.stickers.length > 0")
  // Section title
  .section-title(v-if="stickersBlock.title")
    h6.mb-0 {{ stickersBlock.title.toUpperCase() }}

  // Stickers grid
  .stickers-grid(:class="gridClass")
    .sticker-card(
      v-for="(sticker, index) in stickersBlock.stickers"
      :key="sticker.id || index"
      @click="handleStickerClick(sticker.id)"
    )
      // Sticker image with campaign badge
      .sticker-image-container
        img(
          v-if="sticker.url"
          :src="sticker.url"
          :alt="sticker.title"
          @error="handleImageError"
        ).sticker-image

        // Campaign type badge (if not regular sticker)
        .campaign-badge(
          v-if="sticker.campaignType && sticker.campaignType !== 'Sticker'"
          :class="getCampaignBadgeClass(sticker.campaignType)"
        )
          span {{ sticker.campaignType.toUpperCase() }}

      // Creator info
      .creator-info
        .row.row-xs.align-items-center
          .col-auto
            img(
              :src="getCreatorAvatar(sticker)"
              :alt="getCreatorName(sticker)"
              @error="(e) => handleAvatarError(e, sticker)"
            ).creator-avatar
          .col.lh-1
            span.creator-name(v-if="sticker.entity === 'User' && sticker.user")
              | {{ sticker.user.firstname }} {{ sticker.user.lastname }}
            span.creator-name(v-else-if="sticker.entity === 'Organization' && sticker.organization")
              | {{ sticker.organization.name }}
            span.creator-name(v-else) Spotlight

      // Sticker title
      .sticker-title
        h6.mb-0 {{ sticker.title }}

      // Pricing
      .sticker-price
        span {{ formatPrice(sticker) }}
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { useRouter } from 'vue-router';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { StickersBlock as StickersBlockType } from '../types';

export default defineComponent({
  name: 'StickersBlock',
  props: {
    block: {
      type: Object as PropType<StickersBlockType>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();

    // Placeholder images
    const defaultProfileImage = '/src/assets/images/default-profile.jpeg';
    const orgPlaceholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23e8f5f4" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" font-size="40" fill="%231fbeaf"%3E🏢%3C/text%3E%3C/svg%3E';

    // Computed
    const stickersBlock = computed(() => props.block as StickersBlockType);

    const gridClass = computed(() => {
      const columns = stickersBlock.value.columnsCount;
      // Clamp columns between 1 and 3, default to 2
      const validColumns = columns >= 1 && columns <= 3 ? columns : 2;
      return `grid-columns-${validColumns}`;
    });

    // Methods
    const handleStickerClick = (stickerId: string) => {
      if (!stickerId) return;
      router.push(`/web/sticker/${stickerId}`);
    };

    const getCreatorName = (sticker: any): string => {
      if (sticker.entity === 'User' && sticker.user) {
        return `${sticker.user.firstname} ${sticker.user.lastname}`;
      }
      if (sticker.entity === 'Organization' && sticker.organization) {
        return sticker.organization.name;
      }
      return 'Spotlight';
    };

    const getCreatorAvatar = (sticker: any): string => {
      if (sticker.entity === 'User' && sticker.user?.avatar) {
        return sticker.user.avatar;
      }
      if (sticker.entity === 'Organization' && sticker.organization?.avatar) {
        return sticker.organization.avatar;
      }
      // Return appropriate placeholder
      return sticker.entity === 'Organization' ? orgPlaceholder : defaultProfileImage;
    };

    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      target.style.display = 'none';
    };

    const handleAvatarError = (event: Event, sticker: any) => {
      const target = event.target as HTMLImageElement;
      // Prevent infinite loop
      if (target.src.startsWith('data:image/svg') || target.src === defaultProfileImage) return;
      // Use appropriate placeholder
      target.src = sticker.entity === 'Organization' ? orgPlaceholder : defaultProfileImage;
    };

    const getCampaignBadgeClass = (campaignType: string): string => {
      if (campaignType === 'Sweepstakes') {
        return 'badge-sweepstakes';
      }
      return 'badge-default';
    };

    const formatPrice = (sticker: any): string => {
      // Sweepstakes pricing
      if (sticker.campaignType === 'Sweepstakes') {
        const entryCost = sticker.sweepstakesSticker?.entryCost;
        if (!entryCost || entryCost.length === 0) {
          return 'FREE';
        }
        const maxCost = Math.max(...entryCost);
        return `$0-$${maxCost.toFixed(0)}`;
      }

      // Quick costs pricing
      if (Array.isArray(sticker.quickCosts) && sticker.quickCosts.length > 0) {
        const minCost = Math.min(...sticker.quickCosts);
        const maxCost = Math.max(...sticker.quickCosts);
        if (sticker.quickCosts.length < 2) {
          return `$${minCost.toFixed(0)}`;
        }
        return `$${minCost.toFixed(0)} - $${maxCost.toFixed(0)}`;
      }

      // Price range (for tickets)
      if (sticker.priceRange) {
        return sticker.priceRange;
      }

      // Standard cost
      if (sticker.cost === 0 || !sticker.cost) {
        return 'FREE';
      }

      return `$${sticker.cost.toFixed(0)}`;
    };

    return {
      stickersBlock,
      gridClass,
      handleStickerClick,
      getCreatorName,
      getCreatorAvatar,
      handleImageError,
      handleAvatarError,
      getCampaignBadgeClass,
      formatPrice,
    };
  },
});
</script>

<style lang="scss" scoped>
.stickers-block {
  margin-bottom: 48px;
  padding: 0 24px;

  @media (min-width: 768px) {
    padding: 0 40px;
    margin-bottom: 56px;
  }

  @media (min-width: 1024px) {
    padding: 0 64px;
    margin-bottom: 64px;
  }
}

.section-title {
  margin-bottom: 24px;

  h6 {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #1a1a1a;
    margin: 0;

    @media (min-width: 768px) {
      font-size: 15px;
    }
  }
}

.stickers-grid {
  display: grid;
  gap: 16px;
  width: 100%;
  grid-auto-flow: row;

  &.grid-columns-1 {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    @media (min-width: 768px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 20px;
    }

    @media (min-width: 1024px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 24px;
    }
  }

  &.grid-columns-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 20px;
    }

    @media (min-width: 1024px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 24px;
    }
  }

  &.grid-columns-3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 20px;
    }

    @media (min-width: 1024px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 24px;
    }

    @media (min-width: 1280px) {
      grid-template-columns: repeat(5, minmax(0, 1fr));
    }

    @media (min-width: 1536px) {
      grid-template-columns: repeat(6, minmax(0, 1fr));
    }
  }
}

.sticker-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 0;
  max-width: 100%;
  border-radius: 12px;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);

    .sticker-image-container {
      box-shadow: 0 12px 32px rgba(13, 148, 136, 0.15);
    }

    .sticker-title h6 {
      color: #0d9488;
    }

    .creator-avatar {
      border-color: #0d9488;
    }
  }
}

.sticker-image-container {
  position: relative;
  aspect-ratio: 3 / 4;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 12px;
  max-width: 100%;
  width: 100%;
  background: #f9fafb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    padding: 20px;
  }
}

.sticker-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  .sticker-card:hover & {
    transform: scale(1.05);
  }
}

.campaign-badge {
  position: absolute;
  bottom: 12px;
  left: 0;
  padding: 6px 12px 6px 14px;
  border-radius: 0 8px 8px 0;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  span {
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &.badge-sweepstakes {
    background: linear-gradient(135deg, rgba(251, 191, 36, 0.95) 0%, rgba(245, 158, 11, 0.95) 100%);
    color: #000000;
  }

  &.badge-default {
    background: linear-gradient(135deg, rgba(20, 184, 166, 0.95) 0%, rgba(13, 148, 136, 0.95) 100%);
    color: #ffffff;
  }

  @media (min-width: 768px) {
    span {
      font-size: 12px;
    }
  }
}

.creator-info {
  margin-bottom: 8px;
  padding: 0 4px;

  .row {
    margin: 0;
  }

  .col-auto {
    padding: 0 8px 0 0;
  }

  .col {
    padding: 0;
  }
}

.creator-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  border: 2px solid #e5e7eb;
  transition: border-color 0.2s;
}

.creator-name {
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 14px;
  }
}

.sticker-title {
  margin-bottom: 6px;
  padding: 0 4px;

  h6 {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
    line-height: 1.3;
    transition: color 0.2s;

    @media (min-width: 768px) {
      font-size: 16px;
    }
  }
}

.sticker-price {
  padding: 0 4px 0 6px;

  span {
    color: #0d9488;
    font-size: 14px;
    font-weight: 700;

    @media (min-width: 768px) {
      font-size: 15px;
    }
  }
}
</style>
